import { useParams } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { CodeBlock, InlineCode } from '@/components/ui/code-block'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, THead, Th, Tr, Td } from '@/components/ui/data-table'
import { ApiKeysPanel, SigningKeysPanel } from '@/components/panels/KeyPanels'

const BASE = 'https://partners.atarim.io'

const SECTIONS = [
  { id: 'authentication', label: 'Authentication' },
  { id: 'signing-keys', label: 'SSO signing keys' },
  { id: 'endpoints', label: 'Endpoints' },
  { id: 'buy-links', label: 'Buy links' },
  { id: 'activation-links', label: 'Activation links' },
  { id: 'webhooks', label: 'Webhooks' },
  { id: 'errors', label: 'Errors' },
  { id: 'examples', label: 'Code examples' },
]

/**
 * In-page API reference — partners never leave the dashboard (design.md §5.8). Rendered
 * as the "API & keys" tab of Settings; keys are shown once at generation.
 */
export function ApiDocsPanel() {
  const { slug = '' } = useParams()

  return (
    <>
      <div className="flex gap-8">
        {/* Sticky TOC */}
        <nav className="hidden w-[200px] shrink-0 lg:block" aria-label="API docs sections">
          <ul className="sticky top-6 space-y-1">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="block rounded-lg px-2.5 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground">
                  {s.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="min-w-0 flex-1 space-y-10">
          <section id="authentication" className="scroll-mt-6">
            <ApiKeysPanel slug={slug} />
            <p className="mt-3 text-sm text-muted-foreground">
              Send your key with every request: <InlineCode>x-api-key: pk_live_…</InlineCode>
            </p>
          </section>

          <section id="signing-keys" className="scroll-mt-6">
            <SigningKeysPanel slug={slug} />
          </section>

          <section id="endpoints" className="scroll-mt-6 space-y-6">
            <h3 className="text-base font-semibold">Endpoints</h3>

            <Endpoint method="post" path="/api/v1/licenses" summary="Add an agency licence. Returns the licence with its activation link. A pending licence with a live link holds a Licence Entitlement; if none are free, 100 are added (autoAdded in the response) and Atarim confirms the new volume with you. Returns 409 while the brand is suspended. Send externalRef, your id for the service, to make it safe to retry: a licence already created with it comes back with 200 and existing: true. userEmail pre-fills the activation page.">
              <CodeBlock
                language="json"
                code={`// Request body
{ "label": "Acme Web Agency", "externalRef": "whmcs-service-4821", "userEmail": "owner@agency.com" }

// 201 response
{
  "license": {
    "id": "1f4f6f4f8a1b2c3d",
    "status": "pending",
    "activationUrl": "https://app.atarim.io/partner/activate?token=…",
    "plan": "agency",
    "activationTokenExpiresAt": "2026-06-16T09:00:00.000Z"
  },
  "autoAdded": null,
  "existing": false
}`}
              />
            </Endpoint>

            <Endpoint
              method="get"
              path="/api/v1/licenses"
              summary="List licences, newest first. Optional ?status=pending|active|revoked, ?externalRef=…, ?limit= (max 500) and ?offset=; pass nextOffset back as offset until it is null."
            />
            <Endpoint method="get" path="/api/v1/licenses/{id}" summary="One licence, with activatedEmail once the agency has activated." />
            <Endpoint
              method="post"
              path="/api/v1/licenses/{id}/regenerate"
              summary="A fresh 14-day activation link for a pending licence; the old link stops working."
            />
            <Endpoint
              method="post"
              path="/api/v1/licenses (Pro)"
              summary="Add a Pro licence and report its sale in one call. transaction.externalRef and the Pro sku are required; the Expansion Charge is recorded exactly as POST /api/v1/transactions would. If the sale can't be recorded, no licence is created."
            >
              <CodeBlock language="json" code={`{ "label": "Acme Web Agency", "plan": "pro", "transaction": { "externalRef": "INV-10422", "sku": "pro" } }`} />
            </Endpoint>
            <Endpoint
              method="post"
              path="/api/v1/transactions"
              summary="Report every paid AI Web Agency transaction: new_sale, renewal, credit_pack, pro_upgrade, smb_budget, refund, cancellation. Counted once per externalRef however many times it is sent — a resend returns the original with 200. The list price comes from your price list, or for SMB budgets from your SMB budget scale (send credits, a multiple of 1,000, instead of a sku). Sell an SMB owner the Website Owner plan with new_sale and renewal against its website_owner sku and their smbOwnerId (or smbSiteId); never send what you charged your customer. Report each monthly renewal: a Pro plan or SMB budget with no sale or renewal reported for a Billing Period lapses when it ends."
            >
              <CodeBlock
                language="json"
                code={`// Sale (licenseId for agency products; smbOwnerId, or one of the owner's smbSiteId, for SMB budgets)
{ "externalRef": "INV-10423", "type": "credit_pack", "licenseId": "1f4f6f4f8a1b2c3d", "sku": "credits-1000",
  "occurredAt": "2026-10-09T15:20:00Z" }

// SMB budget: credits instead of a sku (409 if the owner is now an agency's client)
{ "externalRef": "INV-10424", "type": "smb_budget", "smbOwnerId": "9a8b7c6d5e4f3a2b", "credits": 3000 }

// Refund or cancellation of an earlier sale
{ "externalRef": "CN-311", "type": "refund", "reversesRef": "INV-10423" }

// 201 response
{
  "transaction": {
    "externalRef": "INV-10423", "type": "credit_pack", "billingPeriod": "2026-10", "late": false,
    "currency": "EUR", "listPriceMinor": 2000, "chargeMinor": 1000, "customer": { "kind": "licence", "label": "Acme Web Agency" }
  },
  "duplicate": false
}`}
              />
            </Endpoint>
            <Endpoint method="get" path="/api/v1/transactions" summary="Transactions charged in a Billing Period, with the total. Optional ?period=YYYY-MM (default: current)." />
            <Endpoint
              method="post"
              path="/api/v1/sites"
              summary="Enrol a website you host in the SMB offer with just its URL and the owner's email. The first site for an email creates the owner's Atarim account; later sites join it. Free/Paid, the monthly budget, credits and charges belong to the owner, and every site adds 200 one-off free credits. Returns the site, with the Atarim workspaceId the Partner Embed is opened for, and its owner. Returns 409 if the site is already enrolled, the brand is suspended, or the owner's email already has an Atarim account that can't take a host-enrolled site (an agency account, or one that's already an agency's client)."
            >
              <CodeBlock language="json" code={`{ "siteUrl": "https://bluebellbakery.com", "ownerEmail": "owner@bluebellbakery.com" }`} />
            </Endpoint>
            <Endpoint
              method="get"
              path="/api/v1/sites"
              summary="List SMB sites with their ownerId. Optional ?status=active|removed, and ?siteUrl= to find the site on a domain. Sites also appear here when their owner presses Connect in the Atarim plugin on a site you host (source: plugin)."
            />
            <Endpoint
              method="get"
              path="/api/v1/smb-owners"
              summary="List SMB owners with status (free/paid), monthly budget, this month's credits, this period's Expansion Charge and their sites. Optional ?status=free|paid. An owner who joins an agency as its client shows agency and is no longer sold a budget. Report SMB budgets against an owner's id (smbOwnerId)."
            />
            <Endpoint method="delete" path="/api/v1/sites/{id}" summary="Remove a site: all AI activity and emails for it stop. The owner's account, other sites and budget are unaffected." />
            <Endpoint
              method="post"
              path="/api/v1/embed/sessions"
              summary="Start a Partner Embed session from your server. Send a site's workspaceId and the https origin of the page that frames the embed; it must be one of your embed domains (Settings, Embed). Returns a one-time ticket valid for 60 seconds, which your page passes to the embed frame. Returns 403 for a domain not on your list, 404 for a workspace that isn't yours, 409 while the brand is suspended."
            >
              <CodeBlock
                language="json"
                code={`{ "workspaceId": "b1f0c7e2-5a7d-4c1e-9f3a-2d8e6b4a1c90", "origin": "https://billing.example.com", "siteId": "seed-smb-001" }

// 201 response
{ "ticket": "…", "expiresAt": "2026-10-22T14:01:00Z", "sessionId": "…" }`}
              />
            </Endpoint>
            <Endpoint
              method="post"
              path="/api/v1/interest"
              summary="Record a click on an upsell card in your billing system, e.g. { &quot;offer&quot;: &quot;smb&quot;, &quot;customerRef&quot;: &quot;client-1882&quot; }."
            />
            <Endpoint method="delete" path="/api/v1/licenses/{id}" summary="Revoke an active licence or cancel a pending one — immediate. An active agency workspace drops to the free plan. The Licence Entitlement returns from the next Billing Period." />
            <Endpoint
              method="get"
              path="/api/v1/partner"
              summary="Your brand: offers, currency, language, checkout URL, suspension, quota warning, return domains, webhook URL, this period's Licence Entitlement position, and your client-area branding (brand: name, logoUrl, color)."
            />
            <Endpoint
              method="patch"
              path="/api/v1/partner"
              summary="Change any of: quotaWarnPct (everyone with access to this brand is emailed once a Billing Period when this share of your Licence Entitlements is in use; null switches it off), returnDomains (the https origins an activation link may send customers back to; replaces the list) and webhookUrl (a public https URL for signed licence events; null switches them off). Fields you leave out stay as they are."
            >
              <CodeBlock
                language="json"
                code={`{
  "quotaWarnPct": 80,
  "returnDomains": ["https://billing.example.com"],
  "webhookUrl": "https://billing.example.com/modules/servers/atarim/webhook.php"
}`}
              />
            </Endpoint>
            <Endpoint
              method="get"
              path="/api/v1/price-list"
              summary="Your local-currency list prices (amounts in cents). The ETag changes whenever a price changes — send If-None-Match to get 304 Not Modified when nothing has."
            >
              <CodeBlock
                language="json"
                code={`{
  "currency": "EUR",
  "version": 7,
  "items": [
    { "sku": "pro", "kind": "pro", "name": "Pro", "band": "", "credits": null, "amountMinor": 9500 },
    { "sku": "credits-1000", "kind": "credit_pack", "name": "1,000 credits", "band": "", "credits": 1000, "amountMinor": 2000 }
  ],
  "smbBudgetScale": [
    { "fromCredits": 1000, "perThousandMinor": 950 },
    { "fromCredits": 6000, "perThousandMinor": 750 },
    { "fromCredits": 11000, "perThousandMinor": 550 }
  ]
}`}
              />
            </Endpoint>

            <Endpoint
              method="post"
              path="/sso/exchange"
              summary="Exchange a signed request for a single-use SSO token (60-second expiry). Send license_id to open an agency's account, or smb_site_id to open an SMB site owner's. Sign the raw JSON body with HMAC-SHA256 using your signing key and send the hex digest in X-Atarim-Signature."
            >
              <CodeBlock
                language="json"
                code={`// Request body (sign exactly these bytes)
{ "license_id": "…", "user_email": "owner@agency.com", "host_partner_id": "${slug}" }

// or, for an SMB site's owner
{ "smb_site_id": "…", "user_email": "owner@bluebellbakery.com", "host_partner_id": "${slug}" }

// 200 response
{
  "token": "…",
  "redeem_url": "https://app.atarim.io/sso/redeem?token=…",
  "expires_in": 60
}`}
              />
            </Endpoint>
          </section>

          <section id="buy-links" className="scroll-mt-6">
            <h3 className="text-base font-semibold">Buy links</h3>
            <p className="mb-3 mt-1 max-w-3xl text-sm text-muted-foreground">
              When a customer buys a credit pack, a Pro upgrade, the Website Owner plan or an SMB budget inside Atarim, they land on your checkout
              URL with signed <InlineCode>atarim_</InlineCode> parameters: the sku, quantity, price in cents from your price list, currency, return
              URL, an expiry 30 minutes ahead and a nonce, plus the credits, licence, SMB site, account and email when known. On a Pro upgrade, the credits are a shortfall bought with it, not included in the price. Take every{' '}
              <InlineCode>atarim_</InlineCode> parameter except <InlineCode>atarim_signature</InlineCode>, sort by name, join{' '}
              <InlineCode>name=value</InlineCode> pairs with <InlineCode>&amp;</InlineCode> (RFC 3986 encoded, as PHP&apos;s rawurlencode), and
              compare its HMAC-SHA256 with your SSO signing key against <InlineCode>atarim_signature</InlineCode>. Reject expired links, and send
              the customer to <InlineCode>atarim_return_url</InlineCode> after payment.
            </p>
            <CodeBlock
              language="php"
              code={`<?php
parse_str($_SERVER['QUERY_STRING'], $q);
$signed = array_filter($q, fn ($k) => str_starts_with($k, 'atarim_') && $k !== 'atarim_signature', ARRAY_FILTER_USE_KEY);
ksort($signed);
$string = implode('&', array_map(fn ($k, $v) => rawurlencode($k) . '=' . rawurlencode($v), array_keys($signed), $signed));
$valid = hash_equals(hash_hmac('sha256', $string, $signingKey), $q['atarim_signature'] ?? '')
    && (int) $q['atarim_expires'] > time();`}
            />
          </section>

          <section id="activation-links" className="scroll-mt-6">
            <h3 className="text-base font-semibold">Activation links</h3>
            <p className="mb-3 mt-1 max-w-3xl text-sm text-muted-foreground">
              Add <InlineCode>redirect_url</InlineCode> to a licence&apos;s <InlineCode>activationUrl</InlineCode> to send the customer back
              to you, such as your WHMCS client area: straight away if the link is refused, and after onboarding once the licence is
              claimed. Atarim only uses it when its origin is one of your return
              domains (Settings, Return domains, or <InlineCode>returnDomains</InlineCode> on <InlineCode>PATCH /api/v1/partner</InlineCode>);
              any other address is ignored. Treat the customer arriving back as a prompt to check the licence with{' '}
              <InlineCode>GET /api/v1/licenses</InlineCode>, not as proof that it was claimed.
            </p>
            <CodeBlock
              language="text"
              code={`https://app.atarim.io/partner/activate?token=…&redirect_url=https%3A%2F%2Fbilling.example.com%2Fclientarea.php%3Faction%3Dproductdetails%26id%3D4821`}
            />
          </section>

          <section id="webhooks" className="scroll-mt-6">
            <h3 className="text-base font-semibold">Webhooks</h3>
            <p className="mb-3 mt-1 max-w-3xl text-sm text-muted-foreground">
              With a webhook URL set, Atarim POSTs <InlineCode>licence.claimed</InlineCode> when a customer activates a licence. The raw body is
              signed like an SSO request: the hex HMAC-SHA256 with your newest active SSO signing key, in{' '}
              <InlineCode>X-Atarim-Signature</InlineCode>. Reply 2xx within 10 seconds; anything else is retried with backoff for about a day. A
              delivery can arrive more than once, so de-duplicate on <InlineCode>id</InlineCode>. Recent deliveries are listed under Settings,
              Webhook.
            </p>
            <div className="space-y-3">
              <CodeBlock
                language="json"
                code={`// Headers: X-Atarim-Event: licence.claimed, X-Atarim-Delivery: <id>, X-Atarim-Signature: <hex>
{
  "id": "b25efa930c331e226148f06a955a282f",
  "event": "licence.claimed",
  "created_at": "2026-11-05T11:34:11.624Z",
  "data": {
    "license_id": "1f4f6f4f8a1b2c3d",
    "external_ref": "whmcs-service-4821",
    "plan": "agency",
    "account_id": "9001",
    "workspace_id": "4f1c2a7e-8b3d-4c5a-9e2f-1a2b3c4d5e6f",
    "activated_email": "owner@agency.com",
    "activated_at": "2026-11-05T11:34:11.624Z"
  }
}`}
              />
              <CodeBlock
                language="php"
                code={`<?php
$body = file_get_contents('php://input');
$valid = hash_equals(hash_hmac('sha256', $body, $signingKey), $_SERVER['HTTP_X_ATARIM_SIGNATURE'] ?? '');`}
              />
            </div>
          </section>

          <section id="errors" className="scroll-mt-6">
            <h3 className="text-base font-semibold">Errors</h3>
            <p className="mb-3 mt-1 text-sm text-muted-foreground">
              All errors return JSON: <InlineCode>{'{ "error": "…" }'}</InlineCode>
            </p>
            <Table>
              <THead>
                <Th>Status</Th>
                <Th>Meaning</Th>
              </THead>
              <tbody>
                {[
                  ['400', 'Invalid request body or parameters'],
                  ['401', 'Missing/invalid API key, or invalid SSO signature'],
                  ['404', 'Resource not found (or not yours)'],
                  ['409', 'Conflict — e.g. duplicate resource, or the brand is suspended'],
                  ['410', 'Token expired or already used'],
                  ['500', 'Internal error — retry with backoff'],
                ].map(([code, meaning]) => (
                  <Tr key={code}>
                    <Td className="font-mono text-xs">{code}</Td>
                    <Td>{meaning}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </section>

          <section id="examples" className="scroll-mt-6">
            <h3 className="mb-3 text-base font-semibold">Code examples</h3>
            <Tabs defaultValue="curl">
              <TabsList>
                <TabsTrigger value="curl">cURL</TabsTrigger>
                <TabsTrigger value="php">PHP</TabsTrigger>
                <TabsTrigger value="js">JavaScript</TabsTrigger>
              </TabsList>
              <TabsContent value="curl" className="pt-4">
                <CodeBlock
                  language="bash"
                  code={`# Provision a licence
curl -X POST ${BASE}/api/v1/licenses \\
  -H "x-api-key: pk_live_…" \\
  -H "content-type: application/json" \\
  -d '{"label": "Acme Web Agency"}'

# SSO exchange (HMAC-signed)
BODY='{"license_id":"…","user_email":"owner@agency.com","host_partner_id":"${slug}"}'
SIG=$(printf '%s' "$BODY" | openssl dgst -sha256 -hmac "$SIGNING_KEY" | sed 's/^.* //')
curl -X POST ${BASE}/sso/exchange \\
  -H "content-type: application/json" \\
  -H "X-Atarim-Signature: $SIG" \\
  -d "$BODY"`}
                />
              </TabsContent>
              <TabsContent value="php" className="pt-4">
                <CodeBlock
                  language="php"
                  code={`<?php
// Provision a licence
$ch = curl_init('${BASE}/api/v1/licenses');
curl_setopt_array($ch, [
    CURLOPT_POST => true,
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => ['x-api-key: pk_live_…', 'content-type: application/json'],
    CURLOPT_POSTFIELDS => json_encode(['label' => 'Acme Web Agency']),
]);
$license = json_decode(curl_exec($ch), true);

// SSO exchange (HMAC-signed)
$body = json_encode([
    'license_id' => '…',
    'user_email' => 'owner@agency.com',
    'host_partner_id' => '${slug}',
]);
$signature = hash_hmac('sha256', $body, $signingKey);
$ch = curl_init('${BASE}/sso/exchange');
curl_setopt_array($ch, [
    CURLOPT_POST => true,
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => ['content-type: application/json', 'X-Atarim-Signature: ' . $signature],
    CURLOPT_POSTFIELDS => $body,
]);
$sso = json_decode(curl_exec($ch), true);
// Redirect the agency user to $sso['redeem_url'] within 60 seconds`}
                />
              </TabsContent>
              <TabsContent value="js" className="pt-4">
                <CodeBlock
                  language="javascript"
                  code={`// Provision a licence
const res = await fetch('${BASE}/api/v1/licenses', {
  method: 'POST',
  headers: { 'x-api-key': 'pk_live_…', 'content-type': 'application/json' },
  body: JSON.stringify({ label: 'Acme Web Agency' }),
})
const { license } = await res.json()

// SSO exchange (HMAC-signed, Node.js)
import { createHmac } from 'node:crypto'

const body = JSON.stringify({
  license_id: '…',
  user_email: 'owner@agency.com',
  host_partner_id: '${slug}',
})
const signature = createHmac('sha256', process.env.SIGNING_KEY).update(body).digest('hex')
const sso = await fetch('${BASE}/sso/exchange', {
  method: 'POST',
  headers: { 'content-type': 'application/json', 'X-Atarim-Signature': signature },
  body,
}).then((r) => r.json())
// Redirect the agency user to sso.redeem_url within 60 seconds`}
                />
              </TabsContent>
            </Tabs>
          </section>
        </div>
      </div>
    </>
  )
}

function Endpoint({
  method,
  path,
  summary,
  children,
}: {
  method: 'get' | 'post' | 'patch' | 'delete'
  path: string
  summary: string
  children?: React.ReactNode
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
      <div className="flex items-center gap-2">
        <Badge variant={method}>{method.toUpperCase()}</Badge>
        <code className="font-mono text-sm font-medium">{path}</code>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">{summary}</p>
      {children && <div className="mt-3">{children}</div>}
    </div>
  )
}
